import axios from "../../../helpers/axios";
import { generatePublicUrl } from "../../../urlConfig";

/**
 * Документ заказа (бланк для заказчика). Данные заказа сервер берёт из самого
 * заказа; здесь — только правки, PDF и подпись. Файлы закрытые: открываем
 * через axios (токен в заголовке) и показываем как blob.
 */
const enc = encodeURIComponent;
const base = (officeId, orderId) => `/office/${enc(officeId)}/orders/${enc(orderId)}/document`;

export const getApiError = (error, fallback = "Не получилось. Попробуйте ещё раз.") =>
    error?.response?.data?.error || error?.response?.data?.message || error?.message || fallback;

export const fetchOrderDocument = async (officeId, orderId) => (await axios.get(base(officeId, orderId))).data;

export const saveOrderDocument = async (officeId, orderId, patch) => (await axios.put(base(officeId, orderId), patch)).data;

/** Предпросмотр PDF по черновику (сервер ничего не сохраняет) — blob для iframe. */
export const fetchDocumentPreview = async (officeId, orderId, draft, signal) => (
    await axios.post(`${base(officeId, orderId)}/preview`, draft, { responseType: "blob", timeout: 120000, signal })
).data;

/** Бланк до заказа (3D-калькулятор): из позиций корзины, ничего не сохраняет. */
export const fetchDraftDocument = async (payload) => (await axios.post("/print-form/draft", payload, { timeout: 60000 })).data;

export const fetchDraftPdf = async (payload) => (
    await axios.post("/print-form/draft/pdf", payload, { responseType: "blob", timeout: 120000 })
).data;

/** Ошибка из blob-ответа (предпросмотр) — достаём текст. */
export const getBlobApiError = async (error, fallback = "Не удалось собрать предпросмотр") => {
    const data = error?.response?.data;
    if (data instanceof Blob) {
        try {
            const parsed = JSON.parse(await data.text());
            return parsed.error || parsed.message || fallback;
        } catch (_error) {
            return fallback;
        }
    }
    return getApiError(error, fallback);
};

/** Адрес фото товара так, как его показывает сайт (по нему же сервер найдёт файл для PDF). */
export const pictureUrl = (raw) => {
    const value = String(raw || "");
    if (!value) return "";
    if (/^(https?:|blob:|data:)/i.test(value)) return value;
    return generatePublicUrl(value);
};

export const BLANK_CHECKS = [
    ["deliveryCemetery", "Доставка до кладбища"],
    ["deliveryGrave", "Доставка до могилы"],
    ["dismantleCross", "Демонтаж креста, опалубки"],
    ["leveling", "Выравнивание"],
    ["dismantleFormwork", "Демонтаж цементной опалубки"],
    ["channel", "Швеллер"],
];

export const BLANK_OPTIONS = [
    ["font", "Шрифт №"],
    ["portrait", "Портрет"],
    ["ceramic", "Керамогранит"],
    ["niche", "Ниша"],
    ["symbol", "Символ / изображение"],
    ["extraText", "Дополнительный текст"],
];

export const createDocumentPdf = async (officeId, orderId) => (await axios.post(`${base(officeId, orderId)}/pdf`, {}, { timeout: 120000 })).data;

export const deleteDocumentPdf = async (officeId, orderId, pdfId) => (await axios.delete(`${base(officeId, orderId)}/pdf/${enc(pdfId)}`)).data;

export const fetchDocumentPdfBlob = async (officeId, orderId, pdfId) => (
    await axios.get(`${base(officeId, orderId)}/pdf/${enc(pdfId)}`, { responseType: "blob", timeout: 120000 })
).data;

export const signDocument = async (officeId, orderId, payload) => (
    await axios.post(`${base(officeId, orderId)}/sign`, payload, { timeout: 120000 })
).data;

export const uploadPaperSignature = async (officeId, orderId, file, signerName = "") => {
    const data = new FormData();
    data.append("file", file);
    if (signerName) data.append("signerName", signerName);
    return (await axios.post(`${base(officeId, orderId)}/paper`, data, { timeout: 120000 })).data;
};

export const fetchSignatureBlob = async (officeId, orderId) => (
    await axios.get(`${base(officeId, orderId)}/signature`, { responseType: "blob" })
).data;

export const revokeSignature = async (officeId, orderId) => (await axios.delete(`${base(officeId, orderId)}/signature`)).data;

export const fetchOfficeDocuments = async (officeId, params = {}) => (
    await axios.get(`/office/${enc(officeId)}/documents`, { params: { q: params.q || undefined, status: params.status || undefined } })
).data;

/** Листы пустого бланка: ключ, название, что на листе. */
export const BLANK_SHEETS = [
    ["memorial", "Памятник", "эскиз двух сторон, надпись, стела и размеры, подписи"],
    ["fence", "Ограда и прочие изделия", "схема ограды, параметры, калитка, прочие изделия"],
    ["prices", "Список позиций и стоимость", "позиции, цены, итог, предоплата, остаток"],
];

/** Поля, которые можно заполнить на сайте перед печатью (шапка офиса — всегда из офиса). */
export const BLANK_FILL_FIELDS = [
    ["contractNumber", "№ договора", "125"],
    ["contractDate", "Дата договора", "", "date"],
    ["customerName", "Ф.И.О. заказчика", "Иванов Иван Иванович"],
    ["customerPhone", "Телефон", "+7…", "tel"],
    ["customerAddress", "Адрес заказчика", ""],
    ["customerMessenger", "Telegram / MAX", ""],
    ["deceasedName", "Ф.И.О. на камне", ""],
    ["deceasedDates", "Даты жизни", "1950 – 2025"],
    ["epitaph", "Эпитафия", ""],
    ["steleName", "Наименование стелы", ""],
    ["steleArticle", "Артикул", ""],
    ["extras", "Дополнительно", "вазы, лампады, буквы…"],
    ["cemetery", "Кладбище", ""],
    ["sector", "Сектор", ""],
    ["plot", "Участок", ""],
    ["acceptedBy", "Заказ принял", "Фамилия приёмщика"],
];

/**
 * Бланк для печати (PDF blob). sheets — какие листы: memorial, fence, prices.
 * fill — заполненные поля; пусто — «голый» бланк под ручку.
 */
export const fetchBlankPdf = async (officeId, { sheets = [], fill = null, signal } = {}) => {
    const filled = fill && Object.values(fill).some((value) => String(value || "").trim());
    const response = filled
        ? await axios.post(`/office/${enc(officeId)}/document-blank`, { sheets, fill }, { responseType: "blob", timeout: 120000, signal })
        : await axios.get(`/office/${enc(officeId)}/document-blank`, {
            params: { sheets: sheets.join(",") || undefined },
            responseType: "blob",
            timeout: 120000,
            signal,
        });
    return response.data;
};

/** Имя файла пустого бланка по выбранным листам. */
export const blankFileName = (sheets = []) => {
    const parts = [sheets.includes("memorial") ? "памятник" : "", sheets.includes("fence") ? "ограда" : "", sheets.includes("prices") ? "цены" : ""].filter(Boolean);
    return `Бланк заказа (пустой)${parts.length ? ` — ${parts.join(", ")}` : ""}.pdf`;
};

/** Распечатать PDF прямо со страницы: скрытый iframe → окно печати браузера. */
export const printBlob = (blob) => new Promise((resolve) => {
    const url = URL.createObjectURL(blob);
    const frame = document.createElement("iframe");
    frame.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden";
    frame.src = url;
    frame.onload = () => {
        try {
            frame.contentWindow.focus();
            frame.contentWindow.print();
            resolve(true);
        } catch (_error) {
            // Браузер не дал печатать из iframe — открываем PDF во вкладке, там есть «Печать».
            window.open(url, "_blank", "noopener");
            resolve(false);
        }
        setTimeout(() => { frame.remove(); URL.revokeObjectURL(url); }, 120000);
    };
    document.body.appendChild(frame);
});

/**
 * Заказ по бумажному бланку: заполненный от руки бланк сканируют и загружают —
 * создаётся ручной заказ офиса (одна позиция «Заказ по бумажному бланку» на сумму из бланка),
 * а скан прикладывается к его документу как подписанный бумажный бланк.
 */
export const createPaperOrder = async (officeId, { file, customerName = "", customerPhone = "", amount = 0, comment = "" }) => {
    const total = Math.max(0, Math.round(Number(String(amount).replace(/\s+/g, "").replace(",", ".")) || 0));
    const meta = Object.fromEntries(
        Object.entries({ customerName, customerPhone, notes: comment })
            .map(([key, value]) => [key, String(value || "").trim()])
            .filter(([, value]) => !!value)
    );
    const { data } = await axios.post("/office/manualOrder", {
        officeId,
        office: officeId,
        items: [{
            orderItemType: "custom",
            productName: "Заказ по бумажному бланку",
            price: total,
            qty: 1,
        }],
        totalAmount: total,
        orderComment: String(comment || "").trim() || "Заказ по бумажному бланку (скан приложен к документу)",
        officeOrderMeta: meta,
    });
    const order = data?.order || null;
    const orderId = order?._id || order?.id;
    if (!orderId) throw new Error("Заказ не создался");
    let scanError = "";
    try {
        await uploadPaperSignature(officeId, orderId, file, String(customerName || "").trim());
    } catch (error) {
        // Заказ уже есть — не теряем его: сообщаем, что скан нужно приложить ещё раз в документе.
        scanError = getApiError(error, "Скан не загрузился — приложите его в документе заказа");
    }
    return { order, orderId: String(orderId), scanError };
};

export const fetchDocumentSettings = async (officeId) => (await axios.get(`/office/${enc(officeId)}/document-settings`)).data;

export const saveDocumentSettings = async (officeId, payload) => (await axios.put(`/office/${enc(officeId)}/document-settings`, payload)).data;

/** Снимок 3D-сцены (галерея сессии) — превью тоже закрытое. */
/** Масштаб фото и надписи — по умолчанию для всех бланков офиса. */
export const saveBlankDefaults = async (officeId, payload) => (
    await axios.put(`/office/${enc(officeId)}/document-settings/blank-defaults`, payload)
).data;

export const fetchShotThumbBlob = async (officeId, sessionCode, shotId) => (
    await axios.get(`/scene-screenshots/${enc(officeId)}/${enc(sessionCode)}/${enc(shotId)}/thumbnail`, { responseType: "blob" })
).data;

/** Открыть PDF в новой вкладке (blob), без токена в адресе. */
export const openBlob = (blob, filename = "document.pdf", download = false) => {
    const url = URL.createObjectURL(blob);
    if (download) {
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
    } else {
        const opened = window.open(url, "_blank", "noopener");
        if (!opened) {
            const link = document.createElement("a");
            link.href = url;
            link.target = "_blank";
            link.rel = "noopener";
            document.body.appendChild(link);
            link.click();
            link.remove();
        }
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
};

/**
 * Вкладку открываем сразу по нажатию (иначе браузер блокирует всплывающее окно),
 * а PDF подставляем, когда он придёт с сервера. loader — функция, которая вернёт blob.
 */
export const openInNewTab = async (loader, filename = "document.pdf") => {
    const win = window.open("", "_blank");
    if (win) {
        try {
            win.document.title = "Загружаем документ…";
            win.document.body.innerHTML = '<p style="font:16px sans-serif;padding:24px">Загружаем документ…</p>';
        } catch (_error) { }
    }
    try {
        const blob = await loader();
        if (!blob) {
            win?.close();
            return false;
        }
        if (win) {
            const url = URL.createObjectURL(blob);
            win.location.href = url;
            setTimeout(() => URL.revokeObjectURL(url), 120000);
        } else {
            openBlob(blob, filename, true);
        }
        return true;
    } catch (error) {
        win?.close();
        throw error;
    }
};

export const LAYOUT_OPTIONS = [
    ["showDeceasedSection", "Эскиз: лицевая и оборотная сторона"],
    ["showOrderDetails", "Наименование стелы, размеры, отметки"],
    ["showDatesSection", "Сроки установки"],
    ["showGeoSection", "Место установки"],
    ["showOrderPhotos", "Снимки 3D-сцены (отдельная страница)"],
    ["showTerms", "Предупреждения и условия (с подписью)"],
    ["showSignatures", "Подписи"],
    ["showWatermark", "Знак офиса фоном («Лик Памяти» сеткой по листу)"],
];

export const ORDER_KINDS = [
    ["memorial", "Памятник"],
    ["fence", "Ограда"],
    ["mixed", "Смешанный"],
];

export const money = (value) => `${Number(value || 0).toLocaleString("ru-RU", { maximumFractionDigits: 2 })} ₽`;

export const dateText = (value, withTime = false) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return withTime
        ? date.toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
        : date.toLocaleDateString("ru-RU", { day: "2-digit", month: "long", year: "numeric" });
};

export const STATUS_META = {
    not_signed: { label: "Не подписан", tone: "muted" },
    signed: { label: "Подписан", tone: "ok" },
    changed: { label: "Изменён после подписи", tone: "warn" },
};


/** Бланк привязан к конкретной сессии, независимо от открытой вкладки корзины. */
export const fetchBlankSession = async (officeId, sessionId) => (
    await axios.get(`/office-sales-session/session/${enc(officeId)}/${enc(sessionId)}`)
).data.session;

export const saveBlankSession = async (officeId, sessionId, snapshot) => (
    await axios.patch(`/office-sales-session/${enc(officeId)}/${enc(sessionId)}`, { blankDraft: snapshot }, { timeout: 60000 })
).data;

export const createBlankOrder = async (officeId, payload, realPhotos = [], sceneScreens = []) => {
    const data = new FormData();
    data.append('payload', JSON.stringify({ ...payload, scope: 'office', officeId, office: officeId, blankCheckout: true }));
    sceneScreens.slice(0, 5).forEach((blob, index) => data.append('sceneScreens', blob, `scene-${index}.png`));
    realPhotos.slice(0, 5).forEach((file) => data.append('realPhotos', file));
    return (await axios.post('/office/addOrderWithScreens', data, { params: { officeId }, timeout: 120000 })).data.order;
};
